/**
 * Knowledge intents: look a document up (by id or by subject), find every
 * mention of a phrase across the sources, and name the procedure for a job.
 *
 * `search_sources` — the demo's full-text tool — was declared and never
 * called by the regex engine. `find_mentions` is its first caller.
 */

import { BY_ID } from '../../../data/core.ts';
import { healthOf, procFor } from '../../oem-engine.ts';
import { loadOemDomain } from '../../useOemData.ts';
import { clause, route, scopeLabel } from '../answer-kit.ts';
import { TOOLS, loadWorkOrders } from '../tools.ts';
import { cues, type IntentDef } from './types.ts';

/**
 * fork(QA2): words that carry no document signal, so the document scorer does
 * not rank every file that happens to contain "the" or "which".
 */
const STOP_TERMS = new Set([
  'which', 'what', 'where', 'find', 'show', 'covers', 'cover', 'the', 'for',
  'and', 'our', 'document', 'documents', 'procedure', 'manual', 'bulletin',
  'instruction', 'instructions', 'about', 'have', 'there', 'does', 'this',
  'that', 'with', 'from', 'into', 'sop',
]);

const DOC_NOUN = /\b(document|documents|procedure|manual|bulletin|instruction|spec sheet|parts book|sop)\b/;

/** The numbered section of a document excerpt whose heading matches `re`. */
function section(excerpt: string, re: RegExp): string | null {
  const lines = excerpt.split('\n');
  const heading = /^\d+\.\s+[A-Za-z][^.]{0,40}$/;
  const start = lines.findIndex((l) => heading.test(l.trim()) && re.test(l.toLowerCase()));
  if (start < 0) return null;
  const out: string[] = [];
  for (let i = start + 1; i < lines.length; i += 1) {
    const t = lines[i].trim();
    if (heading.test(t) && out.length) break;
    if (t) out.push(t.replace(/^-\s+/, ''));
  }
  const text = out.join(' ').replace(/\s+/g, ' ').trim();
  if (!text) return null;
  // The lake excerpt stops mid-document; say so rather than end mid-sentence.
  return /[.!?)]$/.test(text) ? text : `${text}…`;
}

// ---------------------------------------------------------------------------
// doc_lookup — fork(QA2) branch 19b, plus a document id
// ---------------------------------------------------------------------------

export const docLookup: IntentDef = {
  id: 'doc_lookup',
  family: 'knowledge',
  priority: 60,
  examples: ['What does TB-2026-031 require sites to do?', 'Which document covers the ML-21 coupling?'],
  score: (L, E) => {
    if (E.docIds.length) return 6 + (/require|action|say|cover|about|do\b/.test(L) ? 1 : 0);
    if (DOC_NOUN.test(L) && /which|what|find|where|show|covers?|is there|do we have/.test(L)) return 3;
    return 0;
  },
  async answer({ kit, E, L }) {
    const docIndex = await loadOemDomain('doc-index');
    kit.ran('document_index', E.docIds[0] ? { id: E.docIds[0] } : undefined);
    if (E.docIds[0]) {
      const id = E.docIds[0];
      const doc = docIndex.docs.find((d) => d.file.toUpperCase().startsWith(id));
      const parts = await loadOemDomain('parts');
      const bulletin = parts.bulletins.find((b) => b.id === id);
      if (doc) {
        const wantsActions = /require|action|should|must|do\b|what to/.test(L);
        const actions = wantsActions ? section(doc.excerpt, /required actions|actions|recommend/) : null;
        const summary = section(doc.excerpt, /summary|purpose|scope/);
        const markdown =
          `**${doc.title}**\n\n` +
          (actions
            ? `**Required actions:** ${actions.slice(0, 520)}${actions.length > 520 ? '…' : ''}\n\n`
            : '') +
          (summary ? `${summary.slice(0, actions ? 240 : 420)}${summary.length > (actions ? 240 : 420) ? '…' : ''}\n\n` : '') +
          `Opening it in the data browser.`;
        return kit.done(markdown, [doc.file], {
          navigate: `${route('data') ?? '/integration/data'}?doc=${encodeURIComponent(doc.file)}`,
        });
      }
      if (bulletin) {
        const markdown =
          `**${bulletin.id} — ${bulletin.title}** is in the bulletin service (${bulletin.file}). ` +
          `Its full text is not in the lake; the data browser opens the PDF record.`;
        return kit.done(markdown, [bulletin.file], {
          navigate: `${route('data') ?? '/integration/data'}?doc=${encodeURIComponent(bulletin.file)}`,
        });
      }
      return kit.done(`I can't find **${id}** in the lake or the bulletin service.`, ['doc-index']);
    }

    const terms = L.match(/[a-z0-9][a-z0-9-]{2,}/g) ?? [];
    const scored = docIndex.docs
      .map((d) => {
        const hay = `${d.file} ${d.title} ${d.excerpt}`.toLowerCase();
        const score = terms.reduce(
          (n, t) => (STOP_TERMS.has(t) || !hay.includes(t) ? n : n + (d.title.toLowerCase().includes(t) ? 3 : 1)),
          0,
        );
        return { d, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4);
    if (!scored.length) {
      return kit.done(
        `Nothing in the document lake matches that. Try a document id (TB-2026-031) or a component ("coupling procedure").`,
        ['doc-index'],
      );
    }
    const top = scored[0].d;
    const markdown =
      `**${top.title}**\n\n${top.excerpt.slice(0, 260).trim()}…\n\n` +
      (scored.length > 1
        ? `Also in the lake: ${scored
            .slice(1)
            .map((x) => x.d.title)
            .join('; ')}.\n\n`
        : '') +
      `Opening it in the data browser — the folder it sits in is the connector that filed it.`;
    return kit.done(markdown, [top.file], {
      navigate: `${route('data') ?? '/integration/data'}?doc=${encodeURIComponent(top.file)}`,
    });
  },
};

// ---------------------------------------------------------------------------
// find_mentions — new: the first caller of `search_sources`
// ---------------------------------------------------------------------------

/** The phrase after "mention(s) of", "search for", "find", quoted text first. */
export function searchPhrase(q: string): string | null {
  const quoted = /["“']([^"”']{2,60})["”']/.exec(q)?.[1];
  if (quoted) return quoted.trim();
  const m =
    /\bmentions? of\s+(.+?)[?.!]*$/i.exec(q) ??
    /\b(?:search|look) (?:for|up)\s+(.+?)[?.!]*$/i.exec(q) ??
    /\bwhere (?:is|are|do we mention)\s+(.+?)\s+(?:mentioned|referenced|discussed)\b/i.exec(q) ??
    /\bfind (?:every|all|any)?\s*(?:mentions? of |references? to )?(.+?)[?.!]*$/i.exec(q);
  const p = m?.[1]?.replace(/^(the|a|an)\s+/i, '').trim();
  return p && p.length > 1 ? p : null;
}

export const findMentions: IntentDef = {
  id: 'find_mentions',
  family: 'knowledge',
  priority: 62,
  examples: ['Find every mention of receiver key', 'Search for "wash-down"'],
  score: cues({
    any: [
      /find (every|all|any) (mention|reference)|mentions? of|references? to/,
      /\bsearch (for|the)\b|look up\b|where (is|are) .* (mentioned|referenced|discussed)/,
    ],
    base: 6,
  }),
  async answer({ kit, q }) {
    const phrase = searchPhrase(q) ?? q;
    const hits = await kit.run(TOOLS.search_sources, { query: phrase });
    if (!hits.length) {
      // The demo's search needs every term; retry on the longest word.
      const longest = (phrase.match(/[a-z0-9-]{4,}/gi) ?? []).sort((a, b) => b.length - a.length)[0];
      const retry = longest && longest !== phrase ? await kit.run(TOOLS.search_sources, { query: longest }) : [];
      if (!retry.length) {
        return kit.done(`No document, email, meeting, case or alert mentions **${phrase}**.`, ['search_sources']);
      }
      hits.push(...retry);
    }
    const docIndex = await loadOemDomain('doc-index');
    // A document hit names its title; the source chip wants the file.
    const chip = (source: string) => {
      const title = source.replace(/^[A-Za-z]+: /, '');
      return docIndex.docs.find((d) => d.title.slice(0, 90) === title)?.file ?? title;
    };
    const markdown =
      `**${hits.length} source${hits.length === 1 ? '' : 's'} mention "${phrase}"**\n\n` +
      hits
        .map((h) => `- **${h.source}** — …${h.snippet.slice(0, 150).trim()}…`)
        .join('\n');
    return kit.done(markdown, hits.map((h) => chip(h.source)).slice(0, 6));
  },
};

// ---------------------------------------------------------------------------
// procedure_for — new: "what is on the L-02 follow-up and which procedure applies?"
// ---------------------------------------------------------------------------

export const procedureFor: IntentDef = {
  id: 'procedure_for',
  family: 'knowledge',
  priority: 63,
  examples: ['What is on the L-02 follow-up and which procedure applies?', 'Which procedure covers the T-09 brake job?'],
  needs: ['machine'],
  score: cues({
    any: [/which procedure|what procedure|procedure (applies|for|covers)|work instruction|how do (i|we) (fix|replace|change)|steps (to|for)|follow-?up/],
    base: 6,
  }),
  async answer({ kit, E }) {
    const mid = E.machine as string;
    const [docIndex, workOrders, service] = await Promise.all([
      loadOemDomain('doc-index'),
      loadWorkOrders(),
      loadOemDomain('service'),
    ]);
    kit.ran('open_work', { machine_id: mid });
    const draft = workOrders.find((w) => w.machine_id === mid && /Draft|Released|Scheduled/.test(w.status) && w.wo_type !== 'Preventive');
    const job = service.tapp_jobs.find((j) => j.machine === mid && j.status !== 'Completed');
    const comp =
      E.components[0] && healthOf(mid).find((h) => E.components[0].component.test(h.component))?.component;
    const lastBd = workOrders
      .filter((w) => w.machine_id === mid && w.wo_type === 'Breakdown')
      .sort((a, b) => b.created.localeCompare(a.created))[0];
    const component = comp ?? draft?.component ?? lastBd?.component ?? healthOf(mid).slice().sort((a, b) => a.health_score - b.health_score)[0].component;
    kit.ran('procedure_for', { component });
    const proc = procFor(component, { documents: docIndex.docs });
    const markdown =
      `**${scopeLabel(E).slice(3)} · ${component}**\n\n` +
      (job
        ? `- Follow-up job **${job.job_id}** (${job.job_type}, ${job.status})${job.findings ? ` — ${job.findings}` : ''}.\n`
        : draft
          ? `- Open work: **${draft.wo_id}** ${draft.status} — ${draft.description.split('.')[0]}.\n`
          : lastBd
            ? `- Last breakdown ${lastBd.wo_id} (${lastBd.created.slice(0, 10)}): ${clause(lastBd.root_cause ?? lastBd.description.split('.')[0])}.\n`
            : '') +
      (proc
        ? `- Procedure: **${proc.title}**.\n`
        : `- No controlled procedure in the library for ${component}.\n`) +
      `\nIsolation first: LOTO-UG-003 applies to every underground job; the job card carries the torque values and tools.`;
    kit.focus({ machine: mid, component, mine: BY_ID[mid].mine });
    return kit.done(markdown, [proc?.file ?? 'doc-index', 'work_orders.csv', 'technician app'], {
      navigate: proc ? `${route('data') ?? '/integration/data'}?doc=${encodeURIComponent(proc.file)}` : route('jobcards'),
    });
  },
};
